import React, { useMemo, useState } from 'react';
import { Link, Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import OrderForm from '../../components/orders/OrderForm';
import { useAppData } from '../../contexts/AppDataContext';
import OrderDetail from '../../components/orders/OrderDetail';
import { 
  MagnifyingGlassIcon, 
  PlusIcon,
  EyeIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const OrdersPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('customers');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(5);
  const navigate = useNavigate();
  const { getOrders, getCustomers, getSuppliers, deleteOrder } = useAppData();
  
  // Handle the view details action
  const handleViewOrder = (order) => {
    // Always pass the raw order if available so details have full items
    const payload = order.__raw ? order.__raw : order;
    navigate(`/orders/${order.id}` , { state: { order: payload } });
  };

  // Handle the edit action
  const handleEditOrder = (id) => {
    navigate(`/orders/${id}/edit`);
  };

  // Handle the delete action
  const handleDeleteOrder = async (order) => {
    if (!order) return;
    const id = order.__raw?.id || order.id;
    if (!id) {
      toast.error("ID de commande invalide");
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la commande ${id} ?`)) {
      return;
    }

    // Try deleting from context/localStorage if it is a real order
    try {
      const deleted = await deleteOrder(id);
      if (deleted) {
      toast.success(`Commande supprimée: ${id}`);
      } else {
        // If it's a demo order (no persistence), optimistically remove it from the current UI list
        toast.success(`Commande (démo) supprimée de l'affichage: ${id}`);
      }
    } catch (e) {
      console.error('Erreur suppression commande:', e);
      toast.error("Erreur lors de la suppression de la commande");
    }
  };
  
  // Sample customer orders data (fallback/demo)
  const customerOrders = [
    {
      id: 'CMD-C-2023-001',
      client: 'Supermarché El Amel',
      date: '2023-07-15',
      dueDate: '2023-07-30',
      total: 45600,
      status: 'En cours',
      paymentStatus: 'Partiel',
      items: 12
    },
    {
      id: 'CMD-C-2023-002',
      client: 'Pharmacie Centrale',
      date: '2023-07-14',
      dueDate: '2023-07-28',
      total: 12350,
      status: 'Livré',
      paymentStatus: 'Payé',
      items: 5
    },
    {
      id: 'CMD-C-2023-003',
      client: 'Café Littéraire',
      date: '2023-07-10',
      dueDate: '2023-07-25',
      total: 8750,
      status: 'Livré',
      paymentStatus: 'Payé',
      items: 7
    },
    {
      id: 'CMD-C-2023-004',
      client: 'Ecole Privée Al Manar',
      date: '2023-07-05',
      dueDate: '2023-07-20',
      total: 24800,
      status: 'Annulé',
      paymentStatus: 'Remboursé',
      items: 15
    },
    {
      id: 'CMD-C-2023-005',
      client: 'Bureau d\'Architecture SARL',
      date: '2023-07-18',
      dueDate: '2023-08-02',
      total: 5600,
      status: 'En attente',
      paymentStatus: 'En attente',
      items: 3
    },
    {
      id: 'CMD-C-2023-006',
      client: 'Clinique Santé Plus',
      date: '2023-07-08',
      dueDate: '2023-07-22',
      total: 18900,
      status: 'Livré',
      paymentStatus: 'Payé',
      items: 10
    }
  ];
  
  // Sample supplier orders data (fallback/demo)
  const supplierOrders = [
    {
      id: 'CMD-F-2023-001',
      supplier: 'Algérie Papier',
      date: '2023-07-12',
      expectedDate: '2023-07-25',
      total: 35700,
      status: 'Confirmé',
      deliveryStatus: 'En transit',
      items: 8
    },
    {
      id: 'CMD-F-2023-002',
      supplier: 'Tech Informatique',
      date: '2023-07-10',
      expectedDate: '2023-07-20',
      total: 58200,
      status: 'Reçu',
      deliveryStatus: 'Terminé',
      items: 6
    },
    {
      id: 'CMD-F-2023-003',
      supplier: 'Office Plus',
      date: '2023-07-17',
      expectedDate: '2023-07-31',
      total: 12450,
      status: 'En attente',
      deliveryStatus: 'Non expédié',
      items: 4
    },
    {
      id: 'CMD-F-2023-004',
      supplier: 'Imprimerie Moderne',
      date: '2023-07-14',
      expectedDate: '2023-07-28',
      total: 7800,
      status: 'Confirmé',
      deliveryStatus: 'En préparation',
      items: 3
    },
    {
      id: 'CMD-F-2023-005',
      supplier: 'MeublePro',
      date: '2023-07-05',
      expectedDate: '2023-07-19',
      total: 42500,
      status: 'Annulé',
      deliveryStatus: 'Annulé',
      items: 5
    }
  ];

  // Build real orders from context and normalize to the display shape used by the table
  const customers = useMemo(() => getCustomers(), [getCustomers]);
  const suppliers = useMemo(() => getSuppliers(), [getSuppliers]);
  const ordersFromContext = useMemo(() => getOrders(), [getOrders]);

  const getEntityName = (ent) => {
    if (!ent) return '';
    if (ent.name) return ent.name;
    const first = ent.firstName || '';
    const last = ent.lastName || '';
    return `${first} ${last}`.trim();
  };

  const toTitle = (val) => {
    if (!val) return val;
    const map = {
      'en-attente': 'En attente',
      'en-cours': 'En cours',
      'livre': 'Livré',
      'annule': 'Annulé',
      'pending': 'En attente',
      'paid': 'Payé',
      'partial': 'Partiel',
      'non-expedie': 'Non expédié',
      'en-transit': 'En transit',
      'en-preparation': 'En préparation',
      'termine': 'Terminé',
    };
    return map[val] || val;
  };

  const calcTotal = (order) => {
    if (Array.isArray(order.items) && order.items.length > 0) {
      return order.items.reduce((sum, it) => sum + Number(it.total || (Number(it.price || 0) * Number(it.quantity || 0))), 0);
    }
    return Number(order.total || 0);
  };

  const realCustomerOrders = useMemo(() => {
    return (ordersFromContext || [])
      .filter(o => o.customerId || o.customerName)
      .map(o => {
        const entity = customers.find(c => c.id === o.customerId);
        return {
          id: o.id || o.orderNumber,
          client: o.customerName || getEntityName(entity),
          date: o.date,
          dueDate: o.dueDate,
          total: calcTotal(o),
          status: toTitle(o.status),
          paymentStatus: toTitle(o.paymentStatus),
          items: Array.isArray(o.items) ? o.items.length : 0,
          __raw: o,
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [ordersFromContext, customers]);

  const realSupplierOrders = useMemo(() => {
    return (ordersFromContext || [])
      .filter(o => o.supplierId || o.supplierName)
      .map(o => {
        const entity = suppliers.find(s => s.id === o.supplierId);
        return {
          id: o.id || o.orderNumber,
          supplier: o.supplierName || getEntityName(entity),
          date: o.date,
          expectedDate: o.expectedDate,
          total: calcTotal(o),
          status: toTitle(o.status),
          deliveryStatus: toTitle(o.deliveryStatus),
          items: Array.isArray(o.items) ? o.items.length : 0,
          __raw: o,
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [ordersFromContext, suppliers]);

  // Merge real orders first, then demo ones for a populated view
  const customerDisplayOrders = [...realCustomerOrders, ...customerOrders];
  const supplierDisplayOrders = [...realSupplierOrders, ...supplierOrders];

  // Select current orders based on active tab
  const allOrders = activeTab === 'customers' ? customerDisplayOrders : supplierDisplayOrders;
  
  // Statistics for customer orders
  const customerStats = [
    {
      title: 'Total des Commandes',
      value: customerDisplayOrders.length,
      icon: DocumentTextIcon,
      color: 'from-blue-600 to-indigo-600',
    },
    {
      title: 'Montant Total',
      value: `${customerDisplayOrders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()} DA`,
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-emerald-600',
    },
    {
      title: 'En attente de livraison',
      value: customerDisplayOrders.filter(o => o.status === 'En cours' || o.status === 'En attente').length,
      icon: ClockIcon,
      color: 'from-amber-500 to-orange-600',
    },
  ];
  
  // Statistics for supplier orders
  const supplierStats = [
    {
      title: 'Total des Commandes',
      value: supplierDisplayOrders.length,
      icon: ShoppingCartIcon,
      color: 'from-purple-600 to-indigo-600',
    },
    {
      title: 'Montant Total',
      value: `${supplierDisplayOrders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()} DA`,
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-emerald-600',
    },
    {
      title: 'En attente de réception',
      value: supplierDisplayOrders.filter(o => o.deliveryStatus === 'En transit' || o.deliveryStatus === 'En préparation' || o.deliveryStatus === 'Non expédié').length,
      icon: TruckIcon,
      color: 'from-amber-500 to-orange-600',
    },
  ];
  
  // Order status distribution
  const customerStatusDistribution = [
    { name: 'Livrées', count: customerDisplayOrders.filter(o => o.status === 'Livré').length, color: 'from-green-400 to-green-500' },
    { name: 'En cours', count: customerDisplayOrders.filter(o => o.status === 'En cours').length, color: 'from-blue-400 to-blue-500' },
    { name: 'En attente', count: customerDisplayOrders.filter(o => o.status === 'En attente').length, color: 'from-yellow-400 to-yellow-500' },
    { name: 'Annulées', count: customerDisplayOrders.filter(o => o.status === 'Annulé').length, color: 'from-red-400 to-red-500' },
  ];
  
  const supplierStatusDistribution = [
    { name: 'Reçues', count: supplierDisplayOrders.filter(o => o.status === 'Reçu').length, color: 'from-green-400 to-green-500' },
    { name: 'Confirmées', count: supplierDisplayOrders.filter(o => o.status === 'Confirmé').length, color: 'from-blue-400 to-blue-500' },
    { name: 'En attente', count: supplierDisplayOrders.filter(o => o.status === 'En attente').length, color: 'from-yellow-400 to-yellow-500' },
    { name: 'Annulées', count: supplierDisplayOrders.filter(o => o.status === 'Annulé').length, color: 'from-red-400 to-red-500' },
  ];
  
  const statusDistribution = activeTab === 'customers' ? customerStatusDistribution : supplierStatusDistribution;
  const stats = activeTab === 'customers' ? customerStats : supplierStats;

  // Filter orders based on search term and status filter
  const filteredOrders = allOrders.filter(order => {
    const nameField = activeTab === 'customers' ? order.client : order.supplier;
    const statusField = activeTab === 'customers' ? order.status : order.deliveryStatus;
    
    const matchesSearch = 
      nameField.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatusFilter = statusFilter === 'all' || statusField === statusFilter;
    
    return matchesSearch && matchesStatusFilter;
  });

  // Get status filter options based on active tab
  const getStatusFilterOptions = () => {
    if (activeTab === 'customers') {
      return [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'Livré', label: 'Livré' },
        { value: 'En cours', label: 'En cours' },
        { value: 'En attente', label: 'En attente' },
        { value: 'Annulé', label: 'Annulé' }
      ];
    } else {
      return [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'Terminé', label: 'Terminé' },
        { value: 'En transit', label: 'En transit' },
        { value: 'En préparation', label: 'En préparation' },
        { value: 'Non expédié', label: 'Non expédié' },
        { value: 'Annulé', label: 'Annulé' }
      ];
    }
  };
  
  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  // Render status badge with appropriate color
  const renderStatusBadge = (status) => {
    let bgColor = '';
    let textColor = '';
    
    switch(status) {
      case 'Livré':
      case 'Reçu':
      case 'Terminé':
      case 'Payé':
        bgColor = 'bg-green-500/20';
        textColor = 'text-green-400';
        break;
      case 'En cours':
      case 'Confirmé':
      case 'En transit':
      case 'En préparation':
        bgColor = 'bg-blue-500/20';
        textColor = 'text-blue-400';
        break;
      case 'En attente':
      case 'Non expédié':
      case 'Partiel':
        bgColor = 'bg-yellow-500/20';
        textColor = 'text-yellow-400';
        break;
      case 'Annulé':
      case 'Remboursé':
        bgColor = 'bg-red-500/20';
        textColor = 'text-red-400';
        break;
      default:
        bgColor = 'bg-gray-500/20';
        textColor = 'text-gray-400';
    }
    
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Commandes
              </h1>
              <p className="text-white/60 mt-2">
                Gestion des commandes clients et fournisseurs
              </p>
            </div>
            <Link
              to={`/orders/new?type=${activeTab}`}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouvelle commande</span>
            </Link>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1">
              <button
                onClick={() => {
                  setActiveTab('customers');
                  setStatusFilter('all');
                  setCurrentPage(1);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'customers'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ShoppingCartIcon className="h-5 w-5" />
                <span>Commandes Clients</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('suppliers');
                  setStatusFilter('all');
                  setCurrentPage(1);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'suppliers'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <TruckIcon className="h-5 w-5" />
                <span>Commandes Fournisseurs</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Status Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">
              {activeTab === 'customers' ? 'Statut des Commandes Clients' : 'Statut des Commandes Fournisseurs'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statusDistribution.map((item) => (
                <div 
                  key={item.name}
                  className="bg-white/5 rounded-xl p-4 border border-white/10"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div 
                      className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                      style={{ width: `${(item.count / allOrders.length * 100).toFixed(1)}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {(item.count / allOrders.length * 100).toFixed(1)}% des commandes
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Search & Filters */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={`Rechercher une commande ${activeTab === 'customers' ? 'client' : 'fournisseur'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {getStatusFilterOptions().map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Référence
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {activeTab === 'customers' ? 'Client' : 'Fournisseur'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                      {activeTab === 'customers' ? 'Paiement' : 'Livraison'}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentOrders.map((order, index) => (
                    <motion.tr 
                      key={order.id} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + (index * 0.05) }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-white">{order.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          {activeTab === 'customers' ? order.client : order.supplier}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{order.date}</div>
                        <div className="text-xs text-white/50">
                          Échéance: {activeTab === 'customers' ? order.dueDate : order.expectedDate}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-white">{order.total.toLocaleString()} DA</div>
                        <div className="text-xs text-white/50">{order.items} article(s)</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {renderStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {renderStatusBadge(activeTab === 'customers' ? order.paymentStatus : order.deliveryStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button 
                            onClick={() => handleViewOrder(order)}
                            className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-colors"
                            title="Voir les détails"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleEditOrder(order.id)}
                            className="p-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg text-yellow-400 transition-colors"
                            title="Modifier"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteOrder(order)}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            title="Supprimer"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredOrders.length === 0 && (
              <div className="text-center py-12 text-white/50">
                <DocumentTextIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
                <p className="text-lg font-medium text-white mb-1">Aucune commande trouvée</p>
                <p className="text-white/50">Aucun résultat ne correspond aux critères de recherche.</p>
              </div>
            )}

            {/* Pagination */}
            {filteredOrders.length > 0 && (
              <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                <div className="text-sm text-white/60">
                  Affichage de {indexOfFirstOrder + 1} à {Math.min(indexOfLastOrder, filteredOrders.length)} sur {filteredOrders.length} commandes
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <div className="flex items-center bg-white/5 px-4 rounded-lg text-white">
                    {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      } />
      
      {/* Routes pour la création, le détail et l'édition */}
      <Route path="/new" element={<OrderForm />} />
      <Route path=":id" element={<OrderDetail />} />
      <Route path=":id/edit" element={<OrderForm />} />
    </Routes>
  );
};

export default OrdersPage; 